import { useState, useEffect, useMemo } from 'react';
import { Calendar, MapPin, Users, ArrowRight, Plane } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SearchableSelect } from './SearchableSelect';
import { calculateRoutePrice } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { useData } from '../contexts/DataContext';

const HERO_BG = "https://images.unsplash.com/photo-1747336755277-05b406f5aadf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxQdW50YSUyMENhbmElMjBsdXh1cnklMjByZXNvcnR8ZW58MHx8fHwxNzc1ODU3MzQ2fDA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const { airports, destinations, hotels, setHeroPreFill } = useData();
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [formData, setFormData] = useState({ origin_id: '', origin_type: '', destination_id: '', date: '', passengers: '2' });
  const { t, lang } = useLang();

  const hotelZoneMap = useMemo(() => {
    const map = {};
    hotels.forEach(h => { map[h.id] = h.zone_id; });
    return map;
  }, [hotels]);

  useEffect(() => {
    const fetchPrice = async () => {
      if (formData.origin_id && formData.destination_id) {
        try {
          // If origin is a hotel, resolve to its zone for price lookup
          let originZoneId = formData.origin_id;
          if (formData.origin_type === 'hotel') {
            originZoneId = hotelZoneMap[formData.origin_id] || formData.origin_id;
          }
          const response = await calculateRoutePrice(originZoneId, formData.destination_id);
          setEstimatedPrice(response.data.price);
        } catch (error) { setEstimatedPrice(null); }
      } else { setEstimatedPrice(null); }
    };
    fetchPrice();
  }, [formData.origin_id, formData.destination_id, formData.origin_type, hotelZoneMap]);

  const handleCheckAvailability = (e) => {
    e.preventDefault();
    setHeroPreFill({
      origin_id: formData.origin_id,
      origin_type: formData.origin_type,
      destination_id: formData.destination_id,
      date: formData.date,
      passengers: formData.passengers
    });
    document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
  };

  const originOptions = useMemo(() => {
    const airportLabel = lang === 'es' ? 'Aeropuertos / Puertos' : 'Airports / Ports';
    const hotelLabel = lang === 'es' ? 'Hoteles' : 'Hotels';
    const opts = [];
    opts.push({ isHeader: true, label: airportLabel, value: '__header_airports' });
    airports.forEach(a => opts.push({
      value: `airport_${a.id}`,
      label: lang === 'es' ? (a.name_es || a.name) : a.name,
      group: airportLabel
    }));
    opts.push({ isHeader: true, label: hotelLabel, value: '__header_hotels' });
    hotels.forEach(h => opts.push({
      value: `hotel_${h.id}`,
      label: h.name,
      group: hotelLabel
    }));
    return opts;
  }, [airports, hotels, lang]);

  const destOptions = destinations.map(d => ({ value: d.id, label: lang === 'es' ? (d.name_es || d.name) : d.name }));

  const handleOriginChange = (val) => {
    if (val.startsWith('airport_')) {
      setFormData({ ...formData, origin_id: val.replace('airport_', ''), origin_type: 'airport' });
    } else if (val.startsWith('hotel_')) {
      setFormData({ ...formData, origin_id: val.replace('hotel_', ''), origin_type: 'hotel' });
    }
  };

  const currentOriginVal = formData.origin_id
    ? (formData.origin_type === 'hotel' ? `hotel_${formData.origin_id}` : `airport_${formData.origin_id}`)
    : '';

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="Dominican Republic" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-[#0A192F]/50 to-[#0A192F]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="text-center mb-12">
          <p className="text-cyan-400 text-sm uppercase tracking-[0.2em] mb-4 font-semibold">
            {t('hero_subtitle_top')}
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white tracking-tighter mb-6" data-testid="hero-title">
            {t('hero_title_1')}
            <span className="block gradient-text">{t('hero_title_2')}</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            {t('hero_desc')}
          </p>
        </div>

        <div className="max-w-4xl mx-auto glass rounded-2xl p-6 md:p-8" data-testid="quick-booking-form">
          <form onSubmit={handleCheckAvailability}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="space-y-2">
                <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
                  <Plane className="w-4 h-4 text-cyan-400" />
                  {t('hero_pickup')}
                </label>
                <SearchableSelect
                  options={originOptions}
                  value={currentOriginVal}
                  onValueChange={handleOriginChange}
                  placeholder={t('select_airport')}
                  searchPlaceholder={t('search_placeholder')}
                  testId="hero-origin-select"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-orange-500" />
                  {t('hero_destination')}
                </label>
                <SearchableSelect
                  options={destOptions}
                  value={formData.destination_id}
                  onValueChange={(v) => setFormData({ ...formData, destination_id: v })}
                  placeholder={t('select_destination')}
                  searchPlaceholder={t('search_placeholder')}
                  testId="hero-destination-select"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-cyan-400" />
                  {t('hero_date')}
                </label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-white/5 border-white/10 text-white h-12"
                  data-testid="hero-date-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
                  <Users className="w-4 h-4 text-orange-500" />
                  {t('hero_passengers')}
                </label>
                <Select value={formData.passengers} onValueChange={(v) => setFormData({ ...formData, passengers: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white h-12" data-testid="hero-passengers-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#112240] border-white/10">
                    {[1,2,3,4,5,6,7,8,9,10,11,12,15,20,25,30].map((num) => (
                      <SelectItem key={num} value={num.toString()} className="text-white hover:bg-white/10">
                        {num} {num === 1 ? t('passenger') : t('passengers')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {estimatedPrice !== null && (
              <div className="mb-6 p-4 bg-gradient-to-r from-orange-600/20 to-yellow-500/20 rounded-xl border border-orange-500/30">
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">{t('hero_price_label')}</span>
                  <span className="text-2xl font-bold text-orange-400">${estimatedPrice} USD</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">{t('hero_price_note')}</p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full md:w-auto bg-gradient-to-r from-orange-600 to-yellow-500 text-black font-bold uppercase tracking-wide hover:brightness-110 transition-all shadow-[0_0_20px_rgba(234,88,12,0.4)] h-12 px-8"
              data-testid="hero-check-availability-btn"
            >
              {t('hero_check')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </form>
        </div>

        <div className="flex flex-wrap justify-center gap-8 mt-12">
          <div className="flex items-center gap-2 text-slate-400">
            <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
              <span className="text-cyan-400 font-bold">24/7</span>
            </div>
            <span className="text-sm">{t('hero_available')}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-400">
            <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
              <span className="text-orange-400 font-bold">Pro</span>
            </div>
            <span className="text-sm">{t('hero_drivers')}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-400">
            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
              <span className="text-green-400 font-bold">5★</span>
            </div>
            <span className="text-sm">{t('hero_rated')}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
